import { useState, useEffect, useCallback, useRef } from 'react'
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    ActivityIndicator,
    ScrollView,
    KeyboardAvoidingView,
    Platform,
    Linking,
    StyleSheet,
} from 'react-native'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
    login,
    loginLookup,
    startActivation,
    resendActivation,
    clearError,
} from '../store/slices/authSlice'
import { selectAuthLoading, selectAuthError, selectIsAuthenticated } from '../store/selectors/authSelectors'
import { selectAppLocale } from '../store/selectors/localeSelectors'
import * as authApi from '../api/auth.api'
import { COLORS, SPACING } from '../constants/theme'
import { FormStyles } from '../styles/FormStyles'
import { ButtonStyles } from '../styles/ButtonStyles'
import PasswordField from './PasswordField'
import AuthForm from './AuthForm'
import ForgotPasswordModal from './ForgotPasswordModal'
import { useAppTranslation } from '../i18n/useAppTranslation'
import { useWebInputName } from '../utils/useWebInputName'
import { isValidEmailOrUkrainePhone } from '../utils/authContactUtils'
import { clubBranding } from '../config/clubBranding'
import LocaleSwitcher from './LocaleSwitcher'
import ClubContactLinks from './ClubContactLinks'

interface LoginFormProps {
  onCancel: () => void
  onSuccess: () => void
  onRegister?: () => void
}

type LoginStep = 'identify' | 'password' | 'activation_pending' | 'not_found' | 'social_hint'

interface FormData {
  emailOrPhone: string
  password: string
}

interface FormErrors {
  emailOrPhone?: string
  password?: string
}

const LoginForm = ({ onCancel, onSuccess, onRegister }: LoginFormProps) => {
  const { t } = useAppTranslation()
  const locale = useAppSelector(selectAppLocale)
  const dispatch = useAppDispatch()
  const isLoading = useAppSelector(selectAuthLoading)
  const apiError = useAppSelector(selectAuthError)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  const [step, setStep] = useState<LoginStep>('identify')
  const [formData, setFormData] = useState<FormData>({
    emailOrPhone: '',
    password: ''
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [destinationHint, setDestinationHint] = useState<string | null>(null)
  const [botDeepLink, setBotDeepLink] = useState<string | null>(null)
  const [socialProvider, setSocialProvider] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const usernameRef = useRef<TextInput>(null)
  useWebInputName(usernameRef, 'login-username', 'username')

  const clubDisplayName = clubBranding.name || clubBranding.appTitle

  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  useEffect(() => {
    if (isAuthenticated) {
      onSuccess()
    }
  }, [isAuthenticated, onSuccess])

  const validateIdentify = useCallback((): boolean => {
    const newErrors: FormErrors = {}
    const value = formData.emailOrPhone?.trim()
    if (!value) {
      newErrors.emailOrPhone = t('auth.login.emailOrPhoneRequired')
    } else if (!isValidEmailOrUkrainePhone(value)) {
      newErrors.emailOrPhone = t('auth.login.emailOrPhoneInvalid')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData.emailOrPhone, t])

  const validatePassword = useCallback((): boolean => {
    const newErrors: FormErrors = {}
    if (!formData.password) {
      newErrors.password = t('auth.login.passwordRequired')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData.password, t])

  const handleContinue = async () => {
    if (!validateIdentify()) return
    const emailOrPhone = formData.emailOrPhone.trim()
    try {
      const lookup = await dispatch(loginLookup(emailOrPhone)).unwrap()
      await routeLookupResult(lookup)
    } catch (error) {
      console.error('Lookup failed:', error)
    }
  }

  const routeLookupResult = async (lookup: authApi.LoginLookupResponse) => {
    const { status, destinationHint: hint, provider, message, botDeepLink: lookupBotLink } = lookup
    const emailOrPhone = formData.emailOrPhone.trim()
    setStatusMessage(message ?? null)
    switch (status) {
      case 'PASSWORD_REQUIRED':
        setStep('password')
        break
      case 'ACTIVATION_REQUIRED':
        setDestinationHint(hint ?? null)
        setBotDeepLink(lookupBotLink ?? null)
        setStep('activation_pending')
        try {
          const started = await dispatch(startActivation(emailOrPhone)).unwrap()
          setDestinationHint(started.destinationHint)
          setStatusMessage(started.message)
          if (started.botDeepLink) {
            setBotDeepLink(started.botDeepLink)
          }
        } catch (error) {
          console.error('Activation start failed:', error)
        }
        break
      case 'NOT_FOUND':
        setStep('not_found')
        break
      case 'SOCIAL_SIGN_IN':
        setSocialProvider(provider ?? null)
        setStep('social_hint')
        break
      default:
        break
    }
  }

  const handleLogin = async () => {
    if (!validatePassword()) return
    try {
      await dispatch(login({
        emailOrPhone: formData.emailOrPhone.trim(),
        password: formData.password
      })).unwrap()
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  const handleResendActivation = async () => {
    try {
      const started = await dispatch(resendActivation(formData.emailOrPhone.trim())).unwrap()
      setDestinationHint(started.destinationHint)
      setStatusMessage(started.message)
      if (started.botDeepLink) {
        setBotDeepLink(started.botDeepLink)
      }
    } catch (error) {
      console.error('Resend activation failed:', error)
    }
  }

  const openTelegramBot = () => {
    if (botDeepLink) {
      Linking.openURL(botDeepLink).catch(() => undefined)
    }
  }

  const handleBack = () => {
    setStep('identify')
    setFormData(prev => ({ ...prev, password: '' }))
    setDestinationHint(null)
    setBotDeepLink(null)
    setSocialProvider(null)
    setStatusMessage(null)
    dispatch(clearError())
  }

  const startSocialLogin = async (provider: 'google' | 'apple') => {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('oauthMode', 'login')
      }
      const redirectUri =
        typeof window !== 'undefined'
          ? `${window.location.origin}/oauth/${provider}/callback`
          : `poolcontrol://oauth/${provider}/callback`
      const start = await authApi.socialStart(provider, redirectUri)
      if (typeof window !== 'undefined') {
        window.location.href = start.authorizationUrl
      } else {
        await Linking.openURL(start.authorizationUrl)
      }
    } catch (error) {
      console.error('Unable to start social login', error)
    }
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
    if (apiError) {
      dispatch(clearError())
    }
  }

  const primaryAction = step === 'identify'
    ? handleContinue
    : step === 'password'
      ? handleLogin
      : undefined

  const primaryLabel = step === 'identify'
    ? t('auth.login.continue')
    : step === 'password'
      ? t('auth.login.login')
      : null

  const handlePrimaryPress = () => {
    // requestSubmit() isn't supported before Safari 16 (missing entirely on
    // iOS 15) — fall back to invoking the action directly there.
    if (Platform.OS === 'web' && formRef.current && typeof formRef.current.requestSubmit === 'function') {
      formRef.current.requestSubmit()
    } else {
      primaryAction?.()
    }
  }

  const showCredentialForm = step === 'identify' || step === 'password'

  return (
    <KeyboardAvoidingView
      key={locale}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={FormStyles.container}
    >
      <ScrollView
        contentContainerStyle={FormStyles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={FormStyles.formContainer}>
          <View style={styles.formHeaderRow}>
            <LocaleSwitcher compact />
          </View>
          <View style={FormStyles.formHeader}>
            <Text style={FormStyles.formTitle}>{t('auth.login.title')}</Text>
            <Text style={FormStyles.formSubtitle}>
              {t('auth.login.subtitle', { clubName: clubDisplayName })}
            </Text>
          </View>

          <View style={FormStyles.formBody}>
            <AuthForm
              onSubmit={showCredentialForm ? primaryAction : undefined}
              formRef={formRef}
            >
            {(step === 'identify' || step === 'password' || step === 'activation_pending') && (
              <View style={FormStyles.formGroup}>
                <Text style={FormStyles.formLabel}>{t('auth.login.emailOrPhone')}</Text>
                <TextInput
                  ref={usernameRef}
                  style={[
                    FormStyles.formInput,
                    errors.emailOrPhone && FormStyles.formInputError,
                    step !== 'identify' && { opacity: 0.7 }
                  ]}
                  value={formData.emailOrPhone}
                  onChangeText={(value) => handleChange('emailOrPhone', value)}
                  placeholder={t('auth.login.emailOrPhonePlaceholder')}
                  placeholderTextColor={COLORS.form.placeholder}
                  editable={!isLoading && step === 'identify'}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="default"
                  inputMode="text"
                  autoComplete="username"
                  textContentType="username"
                  nativeID="login-username"
                  importantForAutofill="yes"
                />
                {errors.emailOrPhone && (
                  <Text style={FormStyles.errorMessage}>{errors.emailOrPhone}</Text>
                )}
              </View>
            )}

            {step === 'password' && (
              <>
                <PasswordField
                  label={t('auth.login.password')}
                  value={formData.password}
                  onChangeText={(value) => handleChange('password', value)}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  error={errors.password}
                  editable={!isLoading}
                  autoComplete="current-password"
                  textContentType="password"
                  nativeID="login-password"
                  name="password"
                />

                <TouchableOpacity onPress={() => setForgotPasswordVisible(true)} style={{ alignSelf: 'flex-end', marginBottom: 8 }}>
                  <Text style={{ color: COLORS.white, fontSize: 13 }}>{t('auth.login.forgotPassword')}</Text>
                </TouchableOpacity>
              </>
            )}

            {showCredentialForm && apiError && (
              <View style={FormStyles.apiErrorContainer}>
                <Text style={FormStyles.errorMessage}>{apiError}</Text>
              </View>
            )}

            {showCredentialForm && primaryAction && (
              <View style={FormStyles.formActions}>
                <TouchableOpacity
                  style={[ButtonStyles.btn, ButtonStyles.btnPrimary, ButtonStyles.btnFull, isLoading && ButtonStyles.btnDisabled]}
                  onPress={handlePrimaryPress}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <ActivityIndicator color={COLORS.button.primaryText} />
                  ) : (
                    <Text style={ButtonStyles.btnPrimaryText}>{primaryLabel}</Text>
                  )}
                </TouchableOpacity>
              </View>
            )}
            </AuthForm>

            {step === 'activation_pending' && (
              <View style={{ marginBottom: 16 }}>
                <Text style={FormStyles.formSubtitle}>
                  {t('auth.login.activationSent', {
                    destination: destinationHint
                      ? t('auth.login.activationDestination', { hint: destinationHint })
                      : '',
                  })}
                </Text>
                {statusMessage && (
                  <Text style={[FormStyles.formSubtitle, { marginTop: 8 }]}>{statusMessage}</Text>
                )}
                {botDeepLink && (
                  <TouchableOpacity
                    style={[ButtonStyles.btn, ButtonStyles.btnPrimary, ButtonStyles.btnFull, { marginTop: 12 }]}
                    onPress={openTelegramBot}
                    disabled={isLoading}
                  >
                    <Text style={ButtonStyles.btnPrimaryText}>{t('auth.login.openTelegramBot')}</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  onPress={handleResendActivation}
                  disabled={isLoading}
                  style={{ marginTop: 12 }}
                >
                  <Text style={{ color: COLORS.primaryGreen, fontSize: 13 }}>
                    {t('auth.login.resendActivation')}
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {step === 'not_found' && (
              <View style={{ marginBottom: 16 }}>
                <Text style={FormStyles.errorMessage}>
                  {statusMessage || t('auth.login.notFound')}
                </Text>
              </View>
            )}

            {step === 'social_hint' && (
              <View style={{ marginBottom: 16 }}>
                <Text style={FormStyles.formSubtitle}>
                  {statusMessage || t('auth.login.socialHint', {
                    provider: socialProvider ?? t('auth.login.socialHintFallback'),
                  })}
                </Text>
              </View>
            )}

            {!showCredentialForm && apiError && (
              <View style={FormStyles.apiErrorContainer}>
                <Text style={FormStyles.errorMessage}>{apiError}</Text>
              </View>
            )}


            <View style={FormStyles.formActions}>
              {step !== 'identify' && (
                <TouchableOpacity
                  style={[ButtonStyles.btn, ButtonStyles.btnSecondary, ButtonStyles.btnFull, isLoading && ButtonStyles.btnDisabled]}
                  onPress={handleBack}
                  disabled={isLoading}
                >
                  <Text style={ButtonStyles.btnSecondaryText}>{t('auth.login.back')}</Text>
                </TouchableOpacity>
              )}
              {onRegister && (
                <TouchableOpacity
                  style={[ButtonStyles.btn, ButtonStyles.btnSecondary, ButtonStyles.btnFull, isLoading && ButtonStyles.btnDisabled]}
                  onPress={onRegister}
                  disabled={isLoading}
                >
                  <Text style={ButtonStyles.btnSecondaryText}>{t('common.register')}</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[ButtonStyles.btn, ButtonStyles.btnSecondary, ButtonStyles.btnFull, isLoading && ButtonStyles.btnDisabled]}
                onPress={onCancel}
                disabled={isLoading}
              >
                <Text style={ButtonStyles.btnSecondaryText}>{t('auth.login.cancel')}</Text>
              </TouchableOpacity>
            </View>

            <ClubContactLinks variant="onDark" style={styles.contactLinks} />
          </View>
        </View>
      </ScrollView>

      <ForgotPasswordModal
        visible={forgotPasswordVisible}
        onClose={() => setForgotPasswordVisible(false)}
        initialValue={formData.emailOrPhone}
      />
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
    formHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: SPACING.sm,
    },
    contactLinks: {
        marginTop: SPACING.lg,
    },
})

export default LoginForm
